/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import { getRole, getMacroTemplate } from 'api/roleApi';
import Drawer from 'containers/Drawer';
import {
  EDIT_ROLE,
  CREATE_ROLE,
  CLONE_ROLE,
  SUPER_ADMIN,
} from 'constants/drawerTitles';
import { useSnackbar } from 'hooks';

import RoleForm from '../RoleForm';
import DrawerFooter from '../DrawerFooter';
import { getRolesHelper, updateTabsMacros, roleSettings } from '../utils/crud';
import DeleteRoleIcon from '../DeleteRoleIcon';

import DrawerTitleInfo from './DrawerTitleInfo';

const RoleDrawer = ({
  drawerOpen,
  drawerTitle,
  customPermissions,
  role,
  tabsMacros,
  toggleDrawer,
  setInitialPermissions,
  setRoleRequestBody,
  setEditPermissions,
  setIsLoadingRole,
  setRole,
}) => {
  const [titleInfo, setTitleInfo] = useState(false);

  const setSnackbar = useSnackbar();

  const defineMacros = (payload) => {
    /**
     * Add parent property to roles
     */
    const parentKeys = getRolesHelper(payload);
    /**
     * Add roles to tabsMacros
     */
    const macroSettings = updateTabsMacros(
      parentKeys,
      tabsMacros,
      customPermissions,
    );
    /**
     * convert macro settings to request body permissions
     */
    const permissions = roleSettings(macroSettings);

    setInitialPermissions(permissions);
    setRoleRequestBody(permissions);
    setEditPermissions(macroSettings);
    setTimeout(() => {
      setIsLoadingRole(false);
    }, 100);
  };

  const getRoleTemplate = (name) => {
    getMacroTemplate(name)
      .then((payload) => {
        defineMacros(payload);
      })
      .catch(() => {
        setSnackbar({
          message: `An error occurred while retrieving ${name} role.`,
          severity: 'error',
        });
      });
  };

  const getRoleEdit = (name) => {
    getRole(name)
      .then((payload) => {
        defineMacros(payload);
      })
      .catch(() => {
        setSnackbar({
          message: `An error occurred while retrieving ${name} permissions.`,
          severity: 'error',
        });
      });
  };

  useEffect(() => {
    switch (true) {
      case role:
      case drawerTitle === EDIT_ROLE:
      case drawerTitle === CLONE_ROLE: {
        const { name, user_defined } = role;

        if (!user_defined && role?.name === SUPER_ADMIN) {
          setTitleInfo(false);
          getRoleTemplate('Default');
          break;
        }

        setTitleInfo(false);
        getRoleEdit(name);
        break;
      }
      case drawerTitle === SUPER_ADMIN: // Global Account Manager
        setTitleInfo(true);
        getRoleTemplate('Default');
        break;
      case drawerTitle === CREATE_ROLE:
        getRoleTemplate('Default');
        setTitleInfo(false);
        break;
      default:
        if (!role) break;

        setTitleInfo(true);
        getRoleEdit(role.name);
    }
  }, [drawerOpen]);

  const handleToggle = () => {
    toggleDrawer();
    setRole(null);
  };

  return (
    <Drawer
      title={drawerTitle}
      toggleDrawer={handleToggle}
      open={drawerOpen}
      TitleComponent={titleInfo && DrawerTitleInfo}
    >
      <RoleForm />
      <DrawerFooter
        toggleDrawer={handleToggle}
        DeleteIcon={
          drawerTitle !== CREATE_ROLE &&
          drawerTitle !== CLONE_ROLE &&
          DeleteRoleIcon
        }
      />
    </Drawer>
  );
};

RoleDrawer.propTypes = {
  customPermissions: PropTypes.array,
  drawerOpen: PropTypes.bool,
  drawerTitle: PropTypes.string,
  role: PropTypes.object,
  setEditPermissions: PropTypes.func,
  setInitialPermissions: PropTypes.func,
  setIsLoadingRole: PropTypes.func,
  setRole: PropTypes.func,
  setRoleRequestBody: PropTypes.func,
  tabsMacros: PropTypes.array,
  toggleDrawer: PropTypes.func,
};

export default RoleDrawer;
